import React from 'react'
import { useHistory, useLocation, useParams } from 'react-router-dom'
import { BaseRouteParams } from 'hocs'
import { usePermission } from 'admin/hocs'
import { useHasFeatureFunc } from 'utils/useHasFeature'
import { useListSupersetReports } from 'reports/superset/hooks'
import { UnderlineNavigationTabs } from 'uiComponents/navigation/underlineNavigationTabs'
import InSectionTabsSlot from 'uiComponents/navigation/sectionTabsSlot'

interface ReportsSection {
    name: string
    path: string
    activeKey: string
    shouldShow: boolean
}

export function useReportsSections(): ReportsSection[] {
    const { accountSlug } = useParams<BaseRouteParams>()
    const { hasPermission } = usePermission()
    const hasFeature = useHasFeatureFunc()
    const supersetReports = useListSupersetReports()

    const reports = `/account/${accountSlug}/reports`

    return [
        {
            name: 'Overview',
            path: `${reports}/overview/`,
            activeKey: 'overview',
            shouldShow: hasFeature('ReportsOverview'),
        },
        {
            name: 'Sales Overview',
            path: `${reports}/sales-old`,
            activeKey: 'sales-old',
            shouldShow: hasFeature('sale_reports'),
        },
        ...supersetReports.map((report) => ({
            name: report.menuTitle,
            path: `${reports}/superset/${report.dashboardId}/`,
            activeKey: `superset/${report.dashboardId}`,
            shouldShow: true,
        })),
        {
            name: 'Sales & Finance',
            path: `${reports}/sales`,
            activeKey: 'sales',
            shouldShow: hasFeature('SalesAndFinanceReport'),
        },
        {
            name: 'Sales & Finance Demo',
            path: `${reports}/sales-demo`,
            activeKey: 'sales-demo',
            shouldShow: hasFeature('SalesAndFinanceReportDemo'),
        },
        {
            name: 'Operations',
            path: `${reports}/operations`,
            activeKey: 'operations',
            shouldShow: hasFeature('OperationsReport'),
        },
        {
            name: 'Marketing',
            path: `${reports}/marketing`,
            activeKey: 'marketing',
            shouldShow: hasFeature('MarketingReport'),
        },
        {
            name: 'Barcodes',
            path: `${reports}/barcodes/`,
            activeKey: 'barcodes',
            shouldShow: hasFeature('BarcodesReport'),
        },
        {
            name: 'Discount Codes',
            path: `${reports}/discount_codes/`,
            activeKey: 'discount_codes',
            shouldShow: hasFeature('DiscountCodesReport'),
        },
        {
            name: 'CRM',
            path: `${reports}/crm/`,
            activeKey: 'crm',
            shouldShow: hasFeature('CRMReportPage'),
        },
        {
            name: 'Engage Tools',
            path: `${reports}/engage_tools/`,
            activeKey: 'engage_tools',
            shouldShow: hasPermission('view_wonderbar', accountSlug) && hasFeature('EngageToolsReport'),
        },
    ].filter((section) => section.shouldShow)
}

function ReportsSectionTabs() {
    const location = useLocation()
    const history = useHistory()
    const sections = useReportsSections()

    const segment = location.pathname.split('/reports/')[1]?.replace(/\/+$/, '') ?? ''

    const tabs = sections.map((section) => ({
        name: section.name,
        current: segment === section.activeKey || segment.startsWith(`${section.activeKey}/`),
        href: section.path,
        onClick: () => history.push(section.path),
    }))

    if (tabs.length < 2) {
        return null
    }

    return (
        <InSectionTabsSlot>
            <UnderlineNavigationTabs tabs={tabs} />
        </InSectionTabsSlot>
    )
}

export default ReportsSectionTabs
